import {
    DndContext,
    DragEndEvent,
    DragOverEvent,
    DragStartEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { useEffect } from "react";

import { HotelClinicArrivalSectionFrame, hotelClinicArrivalSections } from "@/constants/hotelClinicArrivalSections";
import {
    ClinicArrivalDragPayload,
    ClinicArrivalDragPayloadTypes,
    ClinicArrivalOverTarget,
    ClinicArrivalOverTargetType,
    useClinicArrivalDndActions,
} from "@/stores/use-clinic-arrival-dnd";
import { useHotelClinicArrivalDate } from "@/stores/use-clinic-arrival-unassigned-passengers-table";

import { useClinicArrivalBoard } from "../hooks/useClinicArrivalBoard";
import ClinicArrivalOverlay from "./ClinicArrivalOverlay";

function ClinicArrivalBoard() {
    const { beginDrag, setTarget, resetSession } = useClinicArrivalDndActions();
    const date = useHotelClinicArrivalDate();
    const { data, movePassenger, isPending: loading, refetch } = useClinicArrivalBoard({ date });

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    useEffect(() => {
        return () => {
            resetSession();
        };
    }, [resetSession]);

    function handleDragStart({ active }: DragStartEvent) {
        const dragData = active.data?.current as ClinicArrivalDragPayload | undefined;
        if (!dragData) return;
        beginDrag(dragData);
    }

    function handleDragOver(event: DragOverEvent) {
        const dragData = event?.active?.data?.current as ClinicArrivalDragPayload | undefined;
        const target = event?.over?.data?.current as ClinicArrivalOverTarget | undefined;
        if (!dragData || !target) {
            setTarget(null);
            return;
        }

        const fromTasks = dragData.type === ClinicArrivalDragPayloadTypes.FROM_TASK;
        const toTasks = target.type === ClinicArrivalOverTargetType.Task;
        if ((toTasks && fromTasks && target.taskId === dragData.taskId) || (!toTasks && !fromTasks)) {
            setTarget(null);
            return;
        }

        setTarget(target);
    }

    function handleDragEnd(event: DragEndEvent) {
        resetSession();
        const dragData = event?.active?.data?.current as ClinicArrivalDragPayload | undefined;
        const target = event?.over?.data?.current as ClinicArrivalOverTarget | undefined;
        if (!dragData || !target) return;
        movePassenger({ dragData, target });
    }

    function handleDragCancel() {
        resetSession();
    }

    return (
        <DndContext
            sensors={sensors}
            modifiers={[restrictToVerticalAxis]}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
        >
            <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden mt-2">
                <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-8 overflow-hidden">
                    {hotelClinicArrivalSections.map((item) => {
                        const { Section, delay, wrapperClassName, ...section } = item;
                        return (
                            <HotelClinicArrivalSectionFrame
                                key={section.text}
                                delay={delay}
                                wrapperClassName={wrapperClassName}
                            >
                                <Section data={data} loading={loading} reloadTable={refetch} {...section} />
                            </HotelClinicArrivalSectionFrame>
                        );
                    })}
                </div>
                <ClinicArrivalOverlay />
            </div>
        </DndContext>
    );
}

export default ClinicArrivalBoard;
